import { ensureGeofences } from "../../../../../../../services/useOrgData";
import React, { useEffect, useMemo, useState } from "react";
import { Box, Popper, Stack } from '@mui/material';
import { useLocation, useNavigate } from "react-router";
import TopBar from "../../../../../../../component/TopBar";
import DataTable from "../../../../../../../component/DataTable";
import MainBox from "../../../../../container/MainBox";
import BtnSecondary from "../../../../../../../component/SecondaryButton";
import { SearchInput, FilterSelect, StatusChip, PersonCell } from "../../../../../../../component/listing";
import { getOrgShifts } from "../../../../../../../services/globalData";
import { getEmployeeDetails } from "../../../../../../../utils/getEmployeeNames";
import { calculateTime } from "../../../../../../../utils/calculateTime";
import { formatTimeString } from "../../../../../../../utils/formatTimeString";
import { isPointInFence } from "../../../../../../../utils/isPointInFence";

import { getDuration } from "../../../../../../../utils/getDuration";
import MapWithMarker from "../../../../../../../component/MapWithMarker";
import { useNotify } from "../../../../../../../component/NotificationProvider";
import { tokens } from "../../../../../../../theme";

const statusOf = (rec = {}) => {
  if (rec?.arrivalTime == null && !rec?.createdAt) return "Absent";
  if (rec?.hasArrivedLate) return "Late";
  return "Present";
};
const STATUS_TONE = { Present: "success", Late: "warning", Absent: "danger" };

const AllEmployeesAttendance = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [fence, setFence] = useState();
  const [search, setSearch] = useState("");
  const [selectedShift, setSelectedShift] = useState("all");
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [mapLocation, setMapLocation] = useState({});

  const shiftOptions = [
    { value: "all", label: "All Shifts" },
    ...(getOrgShifts()?.map((s) => ({ value: s?._id, label: s?.name })) || []),
  ];

  const handleMouseEnter = (event, rec) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
    setMapLocation({ lat: rec?.arrivalLat, lng: rec?.arrivalLong });
  };
  const handleMouseLeave = () => setOpen(false);

  useEffect(() => {
    ensureGeofences().then((f) => setFence(f?.[0]?.geofenceObj?.fences?.[0])).catch(() => {});
    transformArray();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const transformArray = async () => {
    setLoading(true);
    try {
      const transformedArray = state?.employeesAttendanceData?.flatMap(({ date, employees }) =>
        employees.map(({ employeeId, records }) => ({ date, employeeId, records }))
      );
      const employeeIds = transformedArray?.map((d) => d?.employeeId);
      const combinedData = await getEmployeeDetails(transformedArray, employeeIds, "employeeId");
      setData(Array.isArray(combinedData) ? combinedData.reverse() : []);
    } catch (error) {
      notify.error(error?.message || "Failed to load attendance details");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data || []).filter((row) => {
      const name = `${row?.employee?.fname || ""} ${row?.employee?.lname || ""}`.toLowerCase();
      const matchesSearch = !q || name.includes(q) || (row?.employee?.aliasId || "").toLowerCase().includes(q);
      const matchesShift = selectedShift === "all" || row?.records?.[0]?.shift === selectedShift;
      return matchesSearch && matchesShift;
    });
  }, [data, search, selectedShift]);

  return (
    <Box>
      <TopBar MainHeading={"Attendance"}
        breadcrumbs={[{ label: "Attendance" }, { label: "Dashboard", href: "/app/attendance-dashboard" }, { label: "All Employees" }]} />

      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or ID" />
            <FilterSelect value={selectedShift} options={shiftOptions} onChange={(e) => setSelectedShift(e.target.value)} />
          </Stack>
          <BtnSecondary to="/app/attendance-dashboard" sx={{ borderRadius: "8px" }}>
            Back
          </BtnSecondary>
        </Stack>

        <DataTable
          rows={filtered}
          loading={loading}
          empty={{ title: "No attendance details", description: "Nothing to show for the current selection." }}
          getRowId={(row, i) => row?.employeeId || i}
          onRowClick={(row) => row?.employeeId && navigate(`/app/attendance-details/${row.employeeId}`)}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={[
            { key: "date", label: "Date", cellSx: { color: tokens.textMuted }, render: (row) => row?.date },
            {
              key: "employee",
              label: "Employee",
              render: (row) => {
                const emp = row?.employee || {};
                return (
                  <PersonCell
                    name={`${emp.fname || ""} ${emp.lname || ""}`.trim() || "—"}
                    secondary={emp.aliasId || "—"}
                    src={emp.profileImage}
                  />
                );
              },
            },
            {
              key: "location",
              label: "Location",
              // The remote marker opens a map on hover, so the cell must not
              // also carry the row's navigation.
              render: (row) => {
                const rec = row?.records?.[0] || {};
                const isRemote = !isPointInFence(fence, rec?.arrivalLat, rec?.arrivalLong);
                return (
                  <Box onClick={(e) => e.stopPropagation()}>
                    <Box
                      onMouseEnter={isRemote ? (event) => handleMouseEnter(event, rec) : undefined}
                      onMouseLeave={isRemote ? handleMouseLeave : undefined}
                      sx={{
                        display: "inline-block",
                        color: isRemote ? tokens.accent : tokens.textMuted,
                        cursor: isRemote ? "pointer" : "default",
                        textDecoration: isRemote ? "underline" : "none",
                      }}
                    >
                      {isRemote ? "Remote" : "In Office"}
                    </Box>
                  </Box>
                );
              },
            },
            {
              key: "status",
              label: "Status",
              render: (row) => {
                const status = statusOf(row?.records?.[0] || {});
                return <StatusChip label={status} tone={STATUS_TONE[status]} />;
              },
            },
            { key: "in", label: "Clock In", render: (row) => formatTimeString(row?.records?.[0]?.createdAt) || "--" },
            {
              key: "out",
              label: "Clock Out",
              render: (row) => {
                const d = row?.records?.[0]?.departureTime;
                return !isNaN(Number(d)) ? calculateTime(d) : "--";
              },
            },
            {
              key: "hours",
              label: "Working Hours",
              cellSx: { color: tokens.textMuted },
              render: (row) => getDuration(row?.records?.[row?.records?.length - 1]?.totalHours || 0),
            },
          ]}
        />

        <Popper id="popper-id" open={open} anchorEl={anchorEl} placement="bottom-start"
          onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
          sx={{ width: "14rem", border: `1px solid ${tokens.border}`, borderRadius: "10px", boxShadow: "0 4px 12px -4px rgba(42,29,34,0.16)", padding: "0.75rem", backgroundColor: tokens.card, zIndex: 1300 }}>
          <MapWithMarker sx={{ width: "12.5rem", height: "12.5rem", border: `1px solid ${tokens.accent}`, borderRadius: "10px" }}
            location={mapLocation} defaultZoom={16} />
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: "0.5rem", fontWeight: 600, color: tokens.accent, fontSize: "0.8125rem" }}>
            Clock-in location
          </Box>
        </Popper>
      </MainBox>
    </Box>
  );
};

export default AllEmployeesAttendance;
